import { Fragment, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import EmptyState from '../../../../newComponents/emptystate';
import CustomTooltip from '../../../common/CustomTooltip';
import { GetImages } from '../../../../utils/GetImages';
import NameAvatarComponent from '../../../../newComponents/NameAvatar/NameAvatar';
import SimpleSpinner from '../../../loader';
import { TextCss } from '../../../GlobalCss/TextCss';
import { Colors } from '../../../GlobalCss/Colors';
import { ConfirmationPopup } from '../../../../newComponents/confirmation-popup/confirmation-popup-demo';
import { EmailBodyIframe, getEmailDisplayContent } from './EmailBodyIframe';
import Compose from './Compose';
import { NotificationType, PubSub, TopicsEnum } from '../../../../utils/pubsub';
import {
  getOutlookMessage,
  deleteOutlookMessage,
  toggleOutlookMessageStarred,
  OutlookMessage,
  EmailProviderType,
  formatLocalDisplayDateFull,
} from '../personalEmailApi';

// Full inbox parity for a personal Outlook-connected account -- list/open/reply/forward/delete/star.
// Display/layout is borrowed from ShowInboxMessage.tsx (read-only rendering of a single flat message); the
// action-button row (Reply/Forward/Delete/Star) is borrowed from ShowEmail.tsx's existing button row and its
// setReplyId/replytype wiring into Compose, adapted from Gmail's thread ids to Outlook's flat Graph message ids
// (renamed emailId -> uid to match ShowInboxMessage's naming, no behavioral difference -- see the UI consistency
// decisions doc for feature/outlook-provider-p2). Kept as its own component (not folded into ShowEmail/
// ShowInboxMessage) so the working Gmail thread flow and the read-only SMTP flow are never put at risk.

interface ShowOutlookMessageProps {
  uid: string | null;
  onGetEmailList: () => void;
  setEmailId: (id: any) => void;
  // Called alongside onGetEmailList on every successful delete/star-toggle (and threaded into this
  // file's own nested Compose for reply/forward) so the Inbox/Sent/Starred tab counts refresh
  // immediately rather than waiting on the next 30s poll -- mirrors ShowEmail.tsx's existing prop of
  // the same name/signature (bug/email-attachments-outlook item 1). Optional -- omit if the caller
  // doesn't surface tab counts.
  onEmailCountChanged?: () => void;
  // The connected mailbox address -- lets a reply to one of the user's own sent messages target its
  // recipients instead of the user themself.
  connectedEmail?: string;
}

const ShowOutlookMessage = ({ uid, onGetEmailList, setEmailId, onEmailCountChanged = undefined, connectedEmail = "" }: ShowOutlookMessageProps) => {
  const [message, setMessage] = useState<OutlookMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [replyId, setReplyId] = useState<string | undefined>(undefined);
  const [replytype, setReplyType] = useState<string>('');
  const [forwardBody, setForwardBody] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Tracks the most recently requested uid so an in-flight fetch can detect it's been superseded by a faster
  // click on a different message and bail out instead of painting a stale body under the current selection.
  const latestUidRef = useRef<string | null>(null);

  const loadMessage = () => {
    latestUidRef.current = uid;
    if (!uid) {
      setMessage(null);
      return;
    }
    const requestedUid = uid;
    setLoading(true);
    getOutlookMessage(requestedUid)
      .then((result) => {
        if (latestUidRef.current !== requestedUid) return;
        setMessage(result);
      })
      .catch((error) => {
        console.error('Failed to load Outlook message:', error);
        if (latestUidRef.current === requestedUid) setMessage(null);
      })
      .finally(() => {
        if (latestUidRef.current === requestedUid) setLoading(false);
      });
  };

  useEffect(() => {
    setReplyId(undefined);
    setReplyType('');
    loadMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const notifyFailure = (text: string) => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message: text,
    });
  };

  const handleReply = () => {
    if (!uid) return;
    setReplyId(uid);
    setReplyType('reply');
  };

  const handleForward = () => {
    if (!uid) return;
    setReplyId(uid);
    setReplyType('forward');
    // Unlike Gmail (whose forward endpoint requires the full original body composed client-side), Graph's
    // native /forward action already attaches the original message for the recipient -- forwardOutlookMessage's
    // `body` param is only the forwarding comment. Pre-filling the entire original HTML here would both
    // duplicate content the recipient already sees and, for a large email, blow past the request's max URL
    // length (the body/comment is sent as a query param -- see replyOutlookMessage/forwardOutlookMessage).
    setForwardBody('');
  };

  const handleDeleteConfirmed = () => {
    if (!uid) return;
    setIsDeleting(true);
    deleteOutlookMessage(uid)
      .then(() => {
        setShowDeleteConfirm(false);
        setEmailId(null);
        onGetEmailList();
        onEmailCountChanged?.();
      })
      .catch((error) => {
        console.error('Failed to delete Outlook message:', error);
        notifyFailure('Unable to delete email');
      })
      .finally(() => setIsDeleting(false));
  };

  const handleToggleStar = () => {
    if (!uid || !message) return;
    const nextStarred = !message.isStarred;
    setMessage({ ...message, isStarred: nextStarred });
    toggleOutlookMessageStarred(uid, nextStarred)
      .then(() => {
        onGetEmailList();
        onEmailCountChanged?.();
      })
      .catch((error) => {
        console.error('Failed to toggle Outlook message star:', error);
        setMessage((prev) => (prev ? { ...prev, isStarred: !nextStarred } : prev));
        notifyFailure('Unable to update email');
      });
  };

  const menuItems = [
    { id: 1, img: GetImages.EmailForward, name: 'Forward', onClick: handleForward },
    { id: 2, img: GetImages.BlackDelete, name: 'Delete', onClick: () => setShowDeleteConfirm(true) },
  ];

  if (!uid) {
    return (
      <div className="composeContainer">
        <EmptyState
          bodytext="Click on a message from the left to read it"
          isButton={false}
          isBorder={false}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="composeContainer d-flex align-items-center justify-content-center">
        <SimpleSpinner />
      </div>
    );
  }

  if (!message) {
    return (
      <div className="composeContainer">
        <EmptyState
          heading="Message Unavailable"
          bodytext="This message is no longer available (it may have been deleted or moved elsewhere)."
          isButton={false}
          isBorder={false}
        />
      </div>
    );
  }

  return (
    <div className="composeContainer">
      <div className="ShowEmailSection">
        <div className="gap-4 ShowEmailSectionInner">
          <div className="userIcon">
            <NameAvatarComponent
              firstName={(message.fromName ?? message.fromEmail ?? '').split(' ')?.[0] ?? ''}
              lastName={
                (message.fromName ?? message.fromEmail ?? '').split(' ')?.length > 1
                  ? (message.fromName ?? message.fromEmail ?? '').split(' ')[1]
                  : ''
              }
              heightWidth={40}
              shape="square"
              className={TextCss.heading_700_16}
              color={Colors.blue.regular}
              backgroundColor={Colors.blue.lighter}
            />
          </div>
          <div className="email_Deatils_section">
            <div className="email_details_header">
              <div className="email_sender_detials">
                {/* Two senders can share the same display name -- title shows the real address on hover so
                    the user can tell them apart (bug/email-ui-batch2 follow-up). */}
                <p className="sub_heading text-start" title={message.fromEmail}>{message.fromName ?? message.fromEmail}</p>
                {message.toEmail && (
                  <p className="sub_text grey_subtext1 text-start" title={message.toEmail}>To: {message.toEmail}</p>
                )}
                <p className="sub_text grey_subtext1">{formatLocalDisplayDateFull(message.date)}</p>
              </div>
              <div className="email_action">
                <CustomTooltip
                  children={
                    <img
                      src={message.isStarred ? GetImages.FilledBlueStar : GetImages.BlueStar}
                      onClick={handleToggleStar}
                      className="iconstyle"
                      alt="Starred"
                    />
                  }
                  content={message.isStarred ? 'Remove star' : 'Mark as starred'}
                />

                <img
                  src={GetImages.BlueReply}
                  onClick={handleReply}
                  className="iconstyle"
                  alt="Reply"
                />

                <Dropdown>
                  <Dropdown.Toggle style={{ outline: 'none' }} variant="none" className="m-0 p-0">
                    <img src={GetImages.BlueMore} className="iconstyle" alt="More" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="m-2">
                    {menuItems.map((item, index) => (
                      <Fragment key={item.id}>
                        <Dropdown.Item onClick={item.onClick}>
                          <div className="text-decoration-none d-flex flex-row align-items-center gap-2 dropdownActionItem">
                            {item.img && (
                              <img
                                src={item.img}
                                width={20}
                                height={20}
                                alt="Black Email Icon"
                                className="color-white-icon"
                              />
                            )}
                            <p className="sub-text dropdownActionItem">{item.name}</p>
                          </div>
                        </Dropdown.Item>
                        {index !== menuItems.length - 1 && <Dropdown.Divider />}
                      </Fragment>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <p className="normal_text grey_subtext1">{message.subject}</p>
            <EmailBodyIframe body={getEmailDisplayContent(message)} />
          </div>
        </div>
      </div>

      {replyId && (
        <div className="compose-box-fixed mt-3">
          <Compose
            getEmailMessages={onGetEmailList}
            emailId={uid}
            replyId={replyId}
            setReplyId={setReplyId}
            replytype={replytype}
            forwardBody={forwardBody}
            compose={undefined}
            setcompose={undefined}
            providerType={EmailProviderType.Office365}
            onEmailCountChanged={onEmailCountChanged}
            connectedEmail={connectedEmail}
          />
        </div>
      )}

      {showDeleteConfirm && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Email"
          message="Do you want to delete this email?"
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={handleDeleteConfirmed}
          onSecondaryAction={() => setShowDeleteConfirm(false)}
          onClose={() => setShowDeleteConfirm(false)}
          isPrimaryDestructive={true}
          isLoading={isDeleting}
          loadingLabel="Deleting..."
        />
      )}
    </div>
  );
};

export default ShowOutlookMessage;
